import React, { useState } from "react";
import { OptionProps } from "@clearabee/ui-library-native";
import {
  DropdownProp,
  DropdownProps,
  InputTextComponent,
  useDropdownLookup,
} from "components/Composite/DropdownLookup/DropdownLookup";
import { Address, HookComponent } from "@clearabee/ui-sdk";

interface LatLngProps {
  lat: number | null;
  lng: number | null;
}

interface UsePostcodeLookupFieldReturn extends LatLngProps {
  PostcodeInput: InputTextComponent;
  AddressDropdown: DropdownProp;
  isLoading: boolean;
  hasAddresses: boolean;
  handleSubmit: (postcode: string, preventAutoFocus?: boolean) => Promise<void>;
  addressValue: Address | undefined;
  setAddressValue: (value: Address | undefined) => void;
}

interface PostcodeLookupFieldProps extends UsePostcodeLookupFieldProps {
  children: (props: UsePostcodeLookupFieldReturn) => React.ReactElement;
}

interface UsePostcodeLookupFieldProps {
  onOptionSelect: (address: Address) => void;
  getAddresses: (postcode: string) => Promise<{
    addresses: Address[];
    latitude: number | null;
    longitude: number | null;
  }>;
}

const initialLatLng: LatLngProps = {
  lat: null,
  lng: null,
};

export function usePostcodeLookupField({
  onOptionSelect,
  getAddresses,
}: UsePostcodeLookupFieldProps): UsePostcodeLookupFieldReturn {
  const [latlng, setLatLng] = useState(initialLatLng);

  const handlePostcodeLookup = async (
    postcode: string,
  ): Promise<
    {
      options: OptionProps[];
    } & LatLngProps
  > => {
    if (!postcode) {
      setLatLng(initialLatLng);

      return {
        ...initialLatLng,
        options: [],
      };
    }

    const { addresses, latitude, longitude } = await getAddresses(postcode);

    return {
      lat: latitude,
      lng: longitude,
      options: addresses.map(
        ({ line_1, line_2, town_or_city, county }: Address): OptionProps => ({
          label: [line_1, line_2, town_or_city, county]
            .filter(Boolean)
            .join(", "),
          value: JSON.stringify({
            line_1,
            line_2,
            town_or_city,
            county,
          }),
        }),
      ),
    };
  };

  const handleOptionSelect = (
    event: string | React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = typeof event === "string" ? event : event.target.value;

    onOptionSelect(JSON.parse(value) as Address);
  };

  const {
    Dropdown,
    InputText,
    isLoading,
    hasAddresses,
    handleSubmit,
    dropdownValue,
    setDropdownValue,
  } = useDropdownLookup({
    onChange: async (value) => {
      const { options, lat, lng } = await handlePostcodeLookup(
        typeof value === "string" ? value : value.target.value,
      );

      if (!!lat && lat !== 0 && !!lng && lng !== 0) {
        setLatLng({ lat, lng });
      }

      return options;
    },
  });

  const AddressDropdown = React.useMemo(
    () => (props: DropdownProps) =>
      (
        <Dropdown
          {...props}
          onChange={handleOptionSelect}
          isLoading={isLoading}
        />
      ),
    [isLoading, hasAddresses, dropdownValue],
  );

  return {
    lat: latlng.lat,
    lng: latlng.lng,
    AddressDropdown,
    PostcodeInput: InputText,
    isLoading,
    hasAddresses,
    handleSubmit,
    addressValue: dropdownValue ? JSON.parse(dropdownValue) : undefined,
    setAddressValue: (address) =>
      setDropdownValue(address ? JSON.stringify(address) : undefined),
  };
}

export const PostcodeLookupField = ({
  children,
  ...props
}: PostcodeLookupFieldProps): React.ReactElement => {
  return HookComponent({
    hook: usePostcodeLookupField,
    children,
    ...props,
  });
};
